import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { useLoadScript } from "@react-google-maps/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSuitableFor,
  fetchActivities,
  createActivityMaster,
  getGeneralInfo,
  deleteGeneralInfoImages,
  deleteDaysInEachDestination,
} from "../packageService";
import { MIN_PACKAGE_IMAGES } from "../../../../utils/stageManagement";
import { GOOGLE_MAPS_API_KEY } from "../../../../config/googleMaps";
import PlaceSearchInput from "../../../common/PlaceSearchInput";
import TinyEditor from "../../../Editor/TinyEditor";
import Tooltip from "../../../common/Tooltip";
import TimePicker from "../../../common/TimePicker";
import Toster from "../../../common/Toster";
import ImageInput from "../../../common/ImageInput";
import SortablePackageImage from "./SortablePackageImage";
import SortableDestinationRow from "./SortableDestinationRow";
import InlineActivityMasterCreateDialog from "../../../common/InlineActivityMasterCreateDialog";
import { pbAddBtn, pbDeleteBtn } from "../packageBuilderUi";
import { selectMenuPortalProps } from "../../../styles/creatableStyle";

const normalizePackageImage = (img) => {
  if (!img) return null;
  if (typeof img === "string") {
    return { image_url: img, clientKey: `url-${img}` };
  }
  if (img instanceof File || img instanceof Blob) {
    return {
      file: img,
      image_url: URL.createObjectURL(img),
      clientKey: `file-${Date.now()}-${Math.random()}`,
    };
  }
  return {
    id: img.id,
    image_url: img.image_url,
    sort_order: img.sort_order,
    file: img.file || null,
    clientKey: img.clientKey || (img.id ? `existing-${img.id}` : `img-${Date.now()}`),
  };
};


const libraries = ["places"];
const createDestinationClientKey = () =>
  `destination-${Date.now()}-${Math.random()}`;

const GeneralInfo = ({ setGeneralInfoData, draftData, packageid, addSubPackageId, validationErrors, restrictedTourType, selectedGroupPackageCount, onUserEdit }) => {


  // get general info
  const { data: generalInfo } = useQuery({
    queryKey: ["generalInfo", packageid],
    queryFn: () => getGeneralInfo(packageid),
    enabled: !!packageid,
    staleTime: 0,
    cacheTime: 0,
  });

  const generalInfoData = generalInfo?.data;


  // delete general info images
  const queryClient = useQueryClient();
  const { mutate: deleteGeneralInfoImagesMutation } = useMutation({
    mutationFn: (imageId) => {
      console.log('Deleting image with ID:', imageId);
      return deleteGeneralInfoImages(imageId);
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries(["generalInfo", packageid]);
    },

  });

  // delete days in each destination
  const deleteDaysInEachDestinationMutation = useMutation({
    mutationFn: (itemId) => deleteDaysInEachDestination(itemId),
  });

  // State for form validation errors
  const [errors, setErrors] = useState({});
  const [showToster, setShowToster] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [deletingDestinationId, setDeletingDestinationId] = useState(null);
  const hydratedPackageIdRef = useRef(null);

  const notifyUserEdit = () => onUserEdit?.();

  const mapApiGeneralInfoToForm = (data) => ({
    name: data.name || "",
    tour_type: data.tour_type || "",
    minimum_members: data.minimum_members || "",
    maximum_members: data.maximum_members || "",
    starting_location: data.starting_location || "",
    starting_location_latitude: data.starting_location_latitude || "",
    starting_location_longitude: data.starting_location_longitude || "",
    starting_location_place_id: data.starting_location_place_id || "",
    pickup_point: data.pickup_point || "",
    pickup_point_latitude: data.pickup_point_latitude || "",
    pickup_point_longitude: data.pickup_point_longitude || "",
    pickup_point_place_id: data.pickup_point_place_id || "",
    pickup_time: data.pickup_time || "",
    dropoff_point: data.dropoff_point || "",
    dropoff_point_latitude: data.dropoff_point_latitude || "",
    dropoff_point_longitude: data.dropoff_point_longitude || "",
    dropoff_point_place_id: data.dropoff_point_place_id || "",
    dropoff_time: data.dropoff_time || "",
    total_days: data.total_days ?? "",
    total_nights:
      data.total_nights ??
      (data.total_days != null && data.total_days !== ""
        ? Math.max(0, Number(data.total_days) - 1)
        : ""),
    about: data.about || "",
    suitable_ids:
      data.package_suitable?.map((item) => item.suitable_id) ||
      data.suitable_ids ||
      [],
    package_activities:
      data.package_activities?.map((item) => item.activity_id ?? item) || [],
    package_days_each_destinations: (() => {
      const destinations = (data.days_each_destination || data.package_days_each_destinations || []).map(
        (dest) => ({
          id: dest.id || null,
          clientKey:
            dest.clientKey ||
            (dest.id ? `existing-destination-${dest.id}` : createDestinationClientKey()),
          location: dest.destination?.name || dest.location || "",
          no_of_days: dest.no_of_days || 0,
          location_latitude: dest.destination?.latitude || dest.location_latitude || "",
          location_longitude: dest.destination?.longitude || dest.location_longitude || "",
          state_id: dest.destination?.state?.place_id || dest.state_id || "",
          country_id: dest.destination?.state?.country?.place_id || dest.country_id || "",
          place_id: dest.destination?.place_id || dest.place_id || "",
          state_name: dest.destination?.state?.name || dest.state_name || "",
          country_name: dest.destination?.state?.country?.name || dest.country_name || "",
          country_code: dest.destination?.state?.country?.code || dest.country_code || "",
          state_latitude: dest.destination?.state?.latitude || dest.state_latitude || "",
          state_longitude: dest.destination?.state?.longitude || dest.state_longitude || "",
          country_latitude: dest.destination?.state?.country?.latitude || dest.country_latitude || "",
          country_longitude: dest.destination?.state?.country?.longitude || dest.country_longitude || "",
        })
      );
      if (destinations.length > 0) return destinations;
      return [
        {
          id: null,
          clientKey: createDestinationClientKey(),
          location: "",
          no_of_days: 0,
          location_latitude: "",
          location_longitude: "",
          state_id: "",
          country_id: "",
          place_id: "",
          state_name: "",
          country_name: "",
          country_code: "",
          state_latitude: "",
          state_longitude: "",
          country_latitude: "",
          country_longitude: "",
        },
      ];
    })(),
    images:
      (data.images || [])
        .map((img) => normalizePackageImage(img))
        .filter(Boolean) || [],
    package_group_type: data.package_group_id
      ? "existing"
      : data.package_group_type || "new",
    package_group_id:
      data.package_group_id || data.package_group?.id || null,
    current_stage: data.current_stage,
  });


  // Tour type options
  const tourTypeOptions = [
    { value: "private", label: "Private Tour" },
    { value: "fixed_departure", label: "Fixed Departure" },
  ];

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    tour_type: restrictedTourType || "",
    minimum_members: "",
    maximum_members: "",
    starting_location: "",
    starting_location_latitude: "",
    starting_location_longitude: "",
    starting_location_place_id: "",
    pickup_point: "",
    pickup_point_latitude: "",
    pickup_point_longitude: "",
    pickup_point_place_id: "",
    pickup_time: "",
    dropoff_point: "",
    dropoff_point_latitude: "",
    dropoff_point_longitude: "",
    dropoff_point_place_id: "",
    dropoff_time: "",
    total_days: "",
    total_nights: "",
    package_activities: [],
    suitable_ids: [],
    package_days_each_destinations: [{
      id: null, // New destinations don't have an ID
      clientKey: createDestinationClientKey(),
      location: "",
      no_of_days: 0,
      location_latitude: "",
      location_longitude: "",
      state_id: "",
      country_id: "",
      place_id: "",
      state_name: "",
      country_name: "",
      country_code: "",
      state_latitude: "",
      state_longitude: "",
      country_latitude: "",
      country_longitude: ""
    }],
    about: "",
    images: [],
    package_group_type: addSubPackageId ? "existing" : "new",
    package_group_id: addSubPackageId || null,
  });

  // React Query hooks
  const { data: suitableFor = {} } = useQuery({
    queryKey: ["suitableFor"],
    queryFn: fetchSuitableFor,
  });

  const suitableForOptions = (suitableFor.data || []).map((s) => ({
    value: s.id,
    label: s.name,
  }));

  const selectedSuitableForOptions = suitableForOptions.filter((option) =>
    formData.suitable_ids.includes(option.value)
  );

  const { data: activitiesResponse } = useQuery({
    queryKey: ["activities"],
    queryFn: fetchActivities,
  });
  const activities = activitiesResponse?.data || [];

  const activityOptions = activities.map((a) => ({
    value: a.id,
    label: a.name,
  }));

  const selectedActivityOptions = activityOptions.filter((option) =>
    formData.package_activities.some(
      (id) => Number(id) === Number(option.value)
    )
  );

  // Load Google Maps script
  const { loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Reset hydration when editing a different package
  useEffect(() => {
    hydratedPackageIdRef.current = null;
  }, [packageid]);

  // Hydrate form once per package from draft (preferred) or API.
  // Avoid re-applying server data on refetch, which was resetting unsaved duration edits.
  useEffect(() => {
    if (!packageid) return;
    if (hydratedPackageIdRef.current === packageid) return;

    const hasDraft = Boolean(draftData?.name);
    const hasApiData = Boolean(generalInfoData);
    if (!hasDraft && !hasApiData) return;

    hydratedPackageIdRef.current = packageid;
    const source = hasDraft ? draftData : generalInfoData;

    setFormData((prev) => ({
      ...prev,
      ...mapApiGeneralInfoToForm(source),
    }));
  }, [packageid, generalInfoData, draftData]);

  // Keep duration in sync when itinerary days are added/removed on the server.
  useEffect(() => {
    if (!packageid || generalInfoData?.total_days == null) return;

    setFormData((prev) => {
      const nextDays = generalInfoData.total_days ?? prev.total_days;
      const nextNights =
        generalInfoData.total_nights ??
        (Number(nextDays) > 0 ? Number(nextDays) - 1 : prev.total_nights);

      if (
        Number(prev.total_days) === Number(nextDays) &&
        Number(prev.total_nights) === Number(nextNights)
      ) {
        return prev;
      }

      return {
        ...prev,
        total_days: nextDays,
        total_nights: nextNights,
      };
    });
  }, [packageid, generalInfoData?.total_days, generalInfoData?.total_nights]);

  // Update parent with form changes
  useEffect(() => {
    const dataToSend = {
      ...formData,
      current_stage: generalInfoData?.current_stage || 1,
      // Filter out empty destinations
      package_days_each_destinations: formData.package_days_each_destinations.filter(
        dest => dest.location && dest.no_of_days > 0
      )
    };

    // Remove package_group_id if package_group_type is "new"
    if (dataToSend.package_group_type === "new") {
      delete dataToSend.package_group_id;
    }

    setGeneralInfoData(dataToSend);
  }, [formData, setGeneralInfoData, generalInfoData?.current_stage]);

  // is update or create

  useEffect(() => {
    if (generalInfoData && Object.keys(generalInfoData).length > 0) {
      setFormData((prev) => ({
        ...prev,
        isUpdate: true,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        isUpdate: false,
      }));
    }
  }, [generalInfoData]);

  // Update errors when validationErrors prop changes
  useEffect(() => {
    if (validationErrors) {
      try {
        // Parse the error message if it's a string
        const parsedErrors = typeof validationErrors === 'string' ?
          JSON.parse(validationErrors) :
          validationErrors;
        setErrors(parsedErrors);
      } catch (e) {
        console.error('Error parsing validation errors:', e);
        setErrors({});
      }
    } else {
      setErrors({});
    }
  }, [validationErrors]);

  // Add this effect after other useEffect hooks
  useEffect(() => {
    if (formData.tour_type === 'private') {
      setFormData(prev => ({
        ...prev,
        starting_location: '',
        starting_location_latitude: '',
        starting_location_longitude: '',
        starting_location_place_id: '',

      }));
    }
  }, [formData.tour_type]);

  // Keep the handlers simple
  const handleInputChange = (e) => {
    notifyUserEdit();
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleMultiSelectChange = (field, selectedOptions) => {
    notifyUserEdit();
    const selectedIds = selectedOptions.map((option) => option.value);
    setFormData((prev) => ({
      ...prev,
      [field]: selectedIds,
    }));
  };

  const handleActivityCreated = (newId) => {
    notifyUserEdit();
    const id = Number(newId);
    setFormData((prev) => ({
      ...prev,
      package_activities: prev.package_activities.some(
        (existingId) => Number(existingId) === id
      )
        ? prev.package_activities
        : [...prev.package_activities, id],
    }));
  };

  const handleEditorChange = (content) => {
    notifyUserEdit();
    setFormData((prev) => ({
      ...prev,
      about: content,
    }));
  };

  const clearDestinationLocation = (index) => {
    notifyUserEdit();
    setFormData((prev) => {
      const updatedDestinations = [...prev.package_days_each_destinations];
      updatedDestinations[index] = {
        ...updatedDestinations[index],
        location: "",
        location_latitude: "",
        location_longitude: "",
        state_id: "",
        country_id: "",
        place_id: "",
        state_name: "",
        country_name: "",
        country_code: "",
        state_latitude: "",
        state_longitude: "",
        country_latitude: "",
        country_longitude: "",
      };
      return { ...prev, package_days_each_destinations: updatedDestinations };
    });
  };

  const removeDestination = async (index) => {
    notifyUserEdit();
    if (formData.package_days_each_destinations.length <= 1) {
      setShowToster({
        message: "At least one destination is required",
        type: "error",
        show: true,
      });
      return;
    }

    const destinationToDelete = formData.package_days_each_destinations[index];
    const previousDestinations = formData.package_days_each_destinations;

    const removeFromLocalState = () => {
      setFormData((prev) => {
        const updatedDestinations = [...prev.package_days_each_destinations];
        updatedDestinations.splice(index, 1);
        return {
          ...prev,
          package_days_each_destinations: updatedDestinations,
        };
      });
    };

    if (!destinationToDelete.id) {
      removeFromLocalState();
      return;
    }

    removeFromLocalState();
    setDeletingDestinationId(destinationToDelete.id);

    queryClient.setQueryData(["generalInfo", packageid], (old) => {
      if (!old?.data) return old;
      const destinationKey = old.data.days_each_destination
        ? "days_each_destination"
        : "package_days_each_destinations";
      const destinations = old.data[destinationKey] || [];
      return {
        ...old,
        data: {
          ...old.data,
          [destinationKey]: destinations.filter(
            (dest) => Number(dest.id) !== Number(destinationToDelete.id)
          ),
        },
      };
    });

    try {
      await deleteDaysInEachDestinationMutation.mutateAsync(
        destinationToDelete.id
      );
      setShowToster({
        message: "Destination deleted successfully",
        type: "success",
        show: true,
      });
      await queryClient.invalidateQueries(["generalInfo", packageid]);
    } catch (error) {
      setFormData((prev) => ({
        ...prev,
        package_days_each_destinations: previousDestinations,
      }));
      await queryClient.invalidateQueries(["generalInfo", packageid]);
      setShowToster({
        message:
          error.response?.data?.message || "Error deleting destination",
        type: "error",
        show: true,
      });
    } finally {
      setDeletingDestinationId(null);
    }
  };

  const removeImage = async (index) => {
    notifyUserEdit();
    const image = formData.images[index];

    if (image.id) {
      const imageId = image.id;

      if (!imageId) {
        setShowToster({
          message: "Error: Could not find image ID",
          type: "error",
          show: true,
        });
        return;
      }

      try {
        await deleteGeneralInfoImagesMutation(imageId);
        setShowToster({
          message: "Image deleted successfully",
          type: "success",
          show: true,
        });
      } catch (error) {
        setShowToster({
          message: "Error deleting image",
          type: "error",
          show: true,
        });
        return;
      }
    } else if (image.image_url?.startsWith("blob:")) {
      URL.revokeObjectURL(image.image_url);
    }

    setFormData((prev) => {
      const newImages = [...prev.images];
      newImages.splice(index, 1);
      return {
        ...prev,
        images: newImages,
      };
    });
  };

  const moveImage = (dragIndex, hoverIndex) => {
    setFormData((prev) => {
      const nextImages = [...prev.images];
      const [moved] = nextImages.splice(dragIndex, 1);
      nextImages.splice(hoverIndex, 0, moved);
      return {
        ...prev,
        images: nextImages,
      };
    });
  };

  const moveDestination = (dragIndex, hoverIndex) => {
    if (dragIndex === hoverIndex) return;
    notifyUserEdit();
    setFormData((prev) => {
      const nextDestinations = [...prev.package_days_each_destinations];
      const [moved] = nextDestinations.splice(dragIndex, 1);
      nextDestinations.splice(hoverIndex, 0, moved);
      return {
        ...prev,
        package_days_each_destinations: nextDestinations,
      };
    });
  };

  // Helper function to get address component
  const getAddressComponent = (components, type) => {
    const component = components?.find(comp => comp.types.includes(type));
    return {
      long_name: component?.long_name || "",
      short_name: component?.short_name || "",
    };
  };

  // Function to get place_id from query
  const getPlaceIdFromQuery = (query) => {
    return new Promise((resolve) => {
      const service = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      service.findPlaceFromQuery(
        {
          query: query,
          fields: ["place_id", "name"],
        },
        (results, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            results.length > 0
          ) {
            resolve(results[0].place_id);
          } else {
            console.warn(`Place not found for ${query}`);
            resolve("");
          }
        }
      );
    });
  };

  const handlePlaceSelect = (placeDetails, field) => {
    notifyUserEdit();
    if (placeDetails) {
      setFormData((prev) => ({
        ...prev,
        [field]: placeDetails.name,
        [`${field}_latitude`]: placeDetails.lat,
        [`${field}_longitude`]: placeDetails.lng,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: "",
        [`${field}_latitude`]: "",
        [`${field}_longitude`]: "",
      }));
    }
  };

  // Update the error styles for Select components
  const getSelectErrorStyles = (fieldName) => ({
    control: (base, state) => ({
      ...base,
      minHeight: "29px",
      height: "29px",
      borderColor: errors[fieldName] ? "#ef4444" : "#d1d5db",
      borderRadius: "0.5rem",
      boxShadow: state.isFocused ? "0 0 0 1px #2563eb" : "none",
      "&:hover": {
        borderColor: errors[fieldName] ? "#ef4444" : "#d1d5db",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 6px",
      fontSize: "13px",
      height: "27px",
      display: "flex",
      alignItems: "center",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: "0",
      fontSize: "13px",
      height: "27px",
    }),
    placeholder: (base) => ({
      ...base,
      fontSize: "13px",
      fontWeight: "normal",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "13px",
      fontWeight: "500",
    }),
    multiValue: (base) => ({
      ...base,
      fontSize: "11px",
      height: "19px",
    }),
    multiValueLabel: (base) => ({
      ...base,
      fontSize: "11px",
      padding: "0 3px",
    }),
    multiValueRemove: (base) => ({
      ...base,
      fontSize: "11px",
      padding: "0 3px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: "27px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0 6px",
      height: "27px",
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "0 6px",
      height: "27px",
    }),
    menu: (base) => ({
      ...base,
      fontSize: "13px",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  });

  if (loadError) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Google Maps API is not properly configured. Please check your
                API key and ensure the following APIs are enabled:
                <ul className="mt-2 list-disc list-inside">
                  <li>Maps JavaScript API</li>
                  <li>Places API</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="p-0">
        <div className="space-y-6">
          {/* Package Name - Full Width */}
          <div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Package Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name || ""}
                onChange={handleInputChange}
                className={`placeholder:text-sm text-sm font-medium text-gray-800 placeholder:font-normal w-full px-3 py-1 border ${errors.name ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
                placeholder="Enter package name"
              />
              {errors.name && (
                <p className="text-red-500 text-xs mt-1">{errors.name[0]}</p>
              )}
            </div>
          </div>

          {/* Tour Type, Minimum Members, Maximum Members */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tour Type <span className="text-red-500">*</span>
             
                <Tooltip maxWidth="250px" message={`Tour type is locked because existing packages in the group are ${formData.tour_type.charAt(0).toUpperCase() + formData.tour_type.slice(1)}`}  position="right">
                    <i className="fi fi-rr-info text-gray-400 ml-2"></i>
                  </Tooltip>
                
              </label>
              <Select
                name="tour_type"
                isDisabled={true}
                value={
                  tourTypeOptions.find(
                    (option) => option.value === formData.tour_type
                  ) || null
                }
                onChange={(selectedOption) => {
                  notifyUserEdit();
                  setFormData((prev) => ({
                    ...prev,
                    tour_type: selectedOption ? selectedOption.value : "",
                  }));
                }}
                options={tourTypeOptions}
                className={`react-select-container ${errors.tour_type ? "border-red-500" : ""
                  }`}
                classNamePrefix="react-select"
                placeholder="tour type"
                styles={getSelectErrorStyles('tour_type')}
                {...selectMenuPortalProps}
              />
              {errors.tour_type && (
                <p className="text-red-500 text-xs mt-1">{errors.tour_type[0]}</p>
              )}

            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Members <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="minimum_members"
                value={formData.minimum_members || ""}
                onChange={handleInputChange}
                className={`placeholder:text-sm text-sm font-medium text-gray-800 placeholder:font-normal w-full px-3 py-1 border ${errors.minimum_members ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
                placeholder="Enter minimum members"
                min="1"
              />
              {errors.minimum_members && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.minimum_members[0]}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Maximum Members <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="maximum_members"
                value={formData.maximum_members || ""}
                onChange={handleInputChange}
                className={`placeholder:text-sm text-sm font-medium text-gray-800 placeholder:font-normal w-full px-3 py-1 border ${errors.maximum_members ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
                placeholder="Enter maximum members"
                min="1"
              />
              {errors.maximum_members && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.maximum_members[0]}
                </p>
              )}
            </div>
          </div>

          {/* Suitable For and Activities */}
          <div className="grid grid-cols-1 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Suitable For <span className="text-red-500">*</span>
              </label>
              <Select
                isMulti
                isClearable={false}
                options={suitableForOptions}
                value={selectedSuitableForOptions}
                onChange={(selectedOptions) => {
                  handleMultiSelectChange("suitable_ids", selectedOptions || []);
                }}
                className={`react-select-container ${
                  errors.suitable_ids ? "border-red-500" : ""
                }`}
                classNamePrefix="react-select"
                placeholder="Select suitable for..."
                styles={getSelectErrorStyles("suitable_ids")}
                {...selectMenuPortalProps}
              />
              {errors.suitable_ids && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.suitable_ids[0]}
                </p>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Activities <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setActivityDialogOpen(true)}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  + Add Activity
                </button>
              </div>
              <Select
                isMulti
                isClearable={false}
                options={activityOptions}
                value={selectedActivityOptions}
                onChange={(selectedOptions) => {
                  handleMultiSelectChange(
                    "package_activities",
                    selectedOptions || []
                  );
                }}
                className={`react-select-container ${errors.package_activities ? "border-red-500" : ""}`}
                classNamePrefix="react-select"
                placeholder="Select activities..."
                styles={getSelectErrorStyles("package_activities")}
                {...selectMenuPortalProps}
              />
              {errors.package_activities && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.package_activities[0]}
                </p>
              )}
            </div>
          </div>

          {/* Starting Location - Only show for fixed_departure */}
          {formData.tour_type === "fixed_departure" && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Starting Location <span className="text-red-500">*</span>
              </label>
              <PlaceSearchInput
                value={formData.starting_location}
                onChange={(selectedOption) => {
                  handlePlaceSelect(selectedOption, "starting_location");
                }}
                placeholder="Search for a location..."
                hasError={!!errors.starting_location}
              />
              {errors.starting_location && (
                <p className="text-red-500 text-xs mt-1">
                  {errors.starting_location[0]}
                </p>
              )}
            </div>
          )}


          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pickup Point
              </label>
              <PlaceSearchInput
                value={formData.pickup_point}
                onChange={(selectedOption) => {
                  handlePlaceSelect(selectedOption, "pickup_point");
                }}
                placeholder="Search for a location..."
                hasError={!!errors.pickup_point}
              />
              {errors.pickup_point && (
                <p className="text-red-500 text-xs mt-1">{errors.pickup_point[0]}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pickup Time
              </label>
              <TimePicker
                value={formData.pickup_time}
                onChange={(time) => {
                  notifyUserEdit();
                  setFormData((prev) => ({
                    ...prev,
                    pickup_time: time,
                  }));
                }}
                error={errors.pickup_time}
                placeholder="Select pickup time"
              />
              {errors.pickup_time && (
                <p className="text-red-500 text-xs mt-1">{errors.pickup_time[0]}</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dropoff Point
              </label>
              <PlaceSearchInput
                value={formData.dropoff_point}
                onChange={(selectedOption) => {
                  handlePlaceSelect(selectedOption, "dropoff_point");
                }}
                placeholder="Search for a location..."
                hasError={!!errors.dropoff_point}
              />
              {errors.dropoff_point && (
                <p className="text-red-500 text-xs mt-1">{errors.dropoff_point[0]}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dropoff Time
              </label>
              <TimePicker
                value={formData.dropoff_time}
                onChange={(time) => {
                  notifyUserEdit();
                  setFormData((prev) => ({
                    ...prev,
                    dropoff_time: time,
                  }));
                }}
                error={errors.dropoff_time}
                placeholder="Select dropoff time"
              />
              {errors.dropoff_time && (
                <p className="text-red-500 text-xs mt-1">{errors.dropoff_time[0]}</p>
              )}
            </div>
          </div>

        </div>
        {/* Duration */}
        <div className="space-y-5 rounded-xl border border-gray-100 bg-slate-50/40 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
            Duration <i className="fi fi-rr-info text-gray-600 text-sm flex items-center justify-center"></i>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Days <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="total_days"
                value={formData.total_days || ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  const days =
                    raw === "" ? "" : Math.max(1, parseInt(raw, 10) || 1);
                  notifyUserEdit();
                  setFormData((prev) => ({
                    ...prev,
                    total_days: days,
                    total_nights:
                      days === "" ? "" : Math.max(0, Number(days) - 1),
                  }));
                }}
                className={`placeholder:text-sm text-sm font-semibold text-gray-800 placeholder:font-normal w-full px-3 py-1 border ${errors.total_days ? "border-red-500" : "border-gray-300"
                  } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
                placeholder="Enter total days"
              />
              {errors.total_days && (
                <p className="text-red-500 text-xs mt-1">{errors.total_days[0]}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Nights <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="total_nights"
                value={formData.total_nights || ""}
                disabled
                className={`w-full placeholder:text-sm text-sm font-semibold text-gray-800 placeholder:font-normal px-3 py-1 border ${errors.total_nights ? "border-red-500" : "border-gray-300"
                  } rounded-lg bg-gray-100 cursor-not-allowed`}
                placeholder="Auto-calculated"
              />
              {errors.total_nights && (
                <p className="text-red-500 text-xs mt-1">{errors.total_nights[0]}</p>
              )}
            </div>
          </div>
        </div>

        {/* Days in Each Destinations */}
        <div className="space-y-5 rounded-xl border border-gray-100 bg-slate-50/40 p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="flex items-center gap-2 text-sm font-bold text-gray-900">
              Days in Each Destinations{" "}
              <i className="fi fi-rr-info flex items-center justify-center text-sm text-gray-600"></i>
            </h3>
            <p className="text-xs text-gray-500">
              Drag destinations to reorder.
            </p>
          </div>
          <div className="space-y-4">
            {formData.package_days_each_destinations.map((destination, index) => (
              <SortableDestinationRow
                key={destination.clientKey || destination.id}
                index={index}
                moveDestination={moveDestination}
              >
                <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-2">
                  <div>
                    <PlaceSearchInput
                    instanceId={`destination-${index}`}
                    placeId={destination.place_id || null}
                    value={destination.location}
                    types={["locality", "political"]}
                    onChange={async (selectedOption) => {
                      if (!selectedOption) {
                        clearDestinationLocation(index);
                        return;
                      }

                      notifyUserEdit();

                      const state = getAddressComponent(selectedOption.address_components, "administrative_area_level_1");
                        const country = getAddressComponent(selectedOption.address_components, "country");

                        // Get place_ids for state and country
                        const stateId = await getPlaceIdFromQuery(state.long_name);
                        const countryId = await getPlaceIdFromQuery(country.long_name);

                        const updatedDestinations = [...formData.package_days_each_destinations];
                        updatedDestinations[index] = {
                          ...updatedDestinations[index],
                          location: selectedOption.name,
                          no_of_days: destination.no_of_days,
                          location_latitude: selectedOption.lat,
                          location_longitude: selectedOption.lng,
                          state_id: stateId,
                          country_id: countryId,
                          place_id: selectedOption.place_id,
                          state_name: state.long_name,
                          country_name: country.long_name,
                          country_code: country.short_name,
                          state_latitude: "",  // Will be populated when getting place details
                          state_longitude: "", // Will be populated when getting place details
                          country_latitude: "", // Will be populated when getting place details
                          country_longitude: "" // Will be populated when getting place details
                        };

                        // Get coordinates for state and country
                        const service = new window.google.maps.places.PlacesService(document.createElement("div"));

                        // Get state coordinates
                        if (stateId) {
                          service.getDetails({
                            placeId: stateId,
                            fields: ['geometry']
                          }, (statePlace, stateStatus) => {
                            if (stateStatus === window.google.maps.places.PlacesServiceStatus.OK && statePlace.geometry) {
                              updatedDestinations[index].state_latitude = statePlace.geometry.location.lat();
                              updatedDestinations[index].state_longitude = statePlace.geometry.location.lng();
                            }
                          });
                        }

                        // Get country coordinates
                        if (countryId) {
                          service.getDetails({
                            placeId: countryId,
                            fields: ['geometry']
                          }, (countryPlace, countryStatus) => {
                            if (countryStatus === window.google.maps.places.PlacesServiceStatus.OK && countryPlace.geometry) {
                              updatedDestinations[index].country_latitude = countryPlace.geometry.location.lat();
                              updatedDestinations[index].country_longitude = countryPlace.geometry.location.lng();
                            }
                          });
                        }

                        setFormData(prev => ({
                          ...prev,
                          package_days_each_destinations: updatedDestinations
                        }));
                    }}
                    placeholder="Search for a location..."
                    hasError={!!errors.package_days_each_destinations}
                    />
                  </div>
                  <div className="flex gap-2">
                    <input
                    type="number"
                    value={destination.no_of_days || ""}
                    onChange={(e) => {
                      notifyUserEdit();
                      const updatedDestinations = [...formData.package_days_each_destinations];
                      updatedDestinations[index] = {
                        ...updatedDestinations[index],
                        no_of_days: parseInt(e.target.value) || 0
                      };
                      setFormData(prev => ({
                        ...prev,
                        package_days_each_destinations: updatedDestinations
                      }));
                    }}
                    className="flex-1 placeholder:text-sm text-sm font-semibold text-gray-800 placeholder:font-normal px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
                    placeholder="Number of days"
                    min="1"
                    />
                    {formData.package_days_each_destinations.length > 1 && (
                      <button
                      type="button"
                      onClick={() => removeDestination(index)}
                      disabled={
                        deletingDestinationId === destination.id &&
                        Boolean(destination.id)
                      }
                      className={pbDeleteBtn}
                      title="Remove destination"
                      >
                        <i className="fi fi-rr-trash flex items-center justify-center"></i>
                      </button>
                    )}
                  </div>
                </div>
              </SortableDestinationRow>
            ))}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  notifyUserEdit();
                  setFormData(prev => ({
                    ...prev,
                    package_days_each_destinations: [
                      ...prev.package_days_each_destinations,
                      {
                        id: null, // New destinations don't have an ID
                        clientKey: createDestinationClientKey(),
                        location: "",
                        no_of_days: 0,
                        location_latitude: "",
                        location_longitude: "",
                        state_id: "",
                        country_id: "",
                        place_id: "",
                        state_name: "",
                        country_name: "",
                        country_code: "",
                        state_latitude: "",
                        state_longitude: "",
                        country_latitude: "",
                        country_longitude: ""
                      }
                    ]
                  }));
                }}
                className={pbAddBtn}
              >
                <i className="fi fi-rr-plus mr-2"></i>
                Add Destination
              </button>
            </div>
          </div>
          {errors.package_days_each_destinations && (
            <p className="text-red-500 text-xs mt-1">{errors.package_days_each_destinations[0]}</p>
          )}
        </div>

        {/* Overview */}
        <div className="space-y-5 rounded-xl border border-gray-100 bg-slate-50/40 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">
            Overview <span className="text-red-500">*</span>
          </h3>
          <div className={`editor-container border rounded-md ${errors.about ? "border-red-500" : ""}`}>
            <TinyEditor
              className="package-field-text"
              value={formData.about}
              onChange={handleEditorChange}
              height={400}
            />
          </div>
          {errors.about && (
            <p className="text-red-500 text-xs mt-1">{errors.about[0]}</p>
          )}
        </div>

        {/* Image Upload */}
        <div
          id="package-images"
          className={`mb-6 rounded-xl border p-4 ${
            errors.images ? "border-red-300 bg-red-50/30" : "border-transparent"
          }`}
        >
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <label className="block text-sm font-medium text-gray-700">
              Upload Image <span className="text-red-500">*</span>{" "}
              <span className="text-gray-500">
                (Minimum {MIN_PACKAGE_IMAGES} images)
              </span>
            </label>
            <span
              className={`text-xs font-medium ${
                formData.images.length >= MIN_PACKAGE_IMAGES
                  ? "text-green-600"
                  : "text-amber-600"
              }`}
            >
              {formData.images.length}/{MIN_PACKAGE_IMAGES} uploaded
            </span>
          </div>
          <ImageInput
            onChange={(images) => {
              notifyUserEdit();
              const incoming = Array.isArray(images)
                ? images
                : images
                  ? [images]
                  : [];
              const normalized = incoming
                .map((file) => normalizePackageImage(file))
                .filter(Boolean);
              if (normalized.length === 0) return;
              setFormData((prev) => {
                const nextImages = [...prev.images, ...normalized];
                if (nextImages.length >= MIN_PACKAGE_IMAGES) {
                  setErrors((currentErrors) => {
                    if (!currentErrors.images) return currentErrors;
                    const { images: _removed, ...rest } = currentErrors;
                    return rest;
                  });
                }
                return {
                  ...prev,
                  images: nextImages,
                };
              });
            }}
            error={errors.images?.[0] || errors.images}
            showLabel={false}
            label=""
            required={false}
            className=""
            multiple={true}
          />
        </div>
        {/* Image Preview */}
        {formData.images.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-900">
                Attachment Preview
              </h3>
              <p className="text-xs text-gray-500">
                Drag images to reorder. First image is used as the cover.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {formData.images.map((image, index) => (
                <SortablePackageImage
                  key={image.clientKey || image.id || `image-${index}`}
                  image={image}
                  index={index}
                  moveImage={moveImage}
                  onDelete={() => removeImage(index)}
                  label={`Image ${index + 1}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {showToster && (
        <Toster
          message={showToster.message}
          type={showToster.type}
          onClose={() => setShowToster(false)}
        />
      )}

      <InlineActivityMasterCreateDialog
        open={activityDialogOpen}
        onClose={() => setActivityDialogOpen(false)}
        onCreated={handleActivityCreated}
        createFn={createActivityMaster}
        queryKey={["activities"]}
        title="Add Activity"
      />
    </>
  );
};

export default GeneralInfo;
